/*
 * Used inside the Button component
 */

import * as React from 'react';
import PropTypes from 'prop-types';

/* The following maps determine the button icon's base size and at specific viewports.
 * For example, on a mobile viewport the icon can have a 'sm' size, but on desktop
 * it can become larger, 'lg'. This means the icon can scale in size to match the button's scaling size.
 *
 * Unfortunately, these maps are not DRY code. Twin Macro does not allow string interpolation,
 * making it impossible to turn these four maps into a single function, reduce code repetition.
 * https://github.com/ben-rogerson/twin.macro/issues/17
 */

// Leading icon styles - the icon is before the button's text

const leadingBaseMap = {
  xs: '-ml-0.5 mr-2 mb-0.5 h-4 w-4 flex items-center',
  sm: '-ml-0.5 mr-2 h-4 w-4 flex items-center',
  md: '-ml-1 mr-2 h-5 w-5 flex items-center',
  lg: '-ml-1 mr-3 h-5 w-5 flex items-center',
  xl: '-ml-1 mr-3 h-5 w-5 flex items-center',
  xxl: '-ml-1 mr-4 h-6 w-6 flex items-center',
};

const getLeadingBase = base => leadingBaseMap[base];

const leadingMobileMap = {
  xs: 'sm:-ml-0.5 sm:mr-2 sm:h-4 sm:w-4 sm:flex sm:items-center',
  sm: 'sm:-ml-0.5 sm:mr-2 sm:h-4 sm:w-4 sm:flex sm:items-center',
  md: 'sm:-ml-1 sm:mr-2 sm:h-5 sm:w-5 sm:flex sm:items-center',
  lg: 'sm:-ml-1 sm:mr-3 sm:h-5 sm:w-5 sm:flex sm:items-center',
  xl: 'sm:-ml-1 sm:mr-3 sm:h-5 sm:w-5 sm:flex sm:items-center',
  xxl: 'sm:-ml-1 sm:mr-4 sm:h-6 sm:w-6 sm:flex sm:items-center',
};

const getLeadingMobile = mobile => (mobile ? leadingMobileMap[mobile] : null);

const leadingTabletMap = {
  xs: 'md:-ml-0.5 md:mr-2 md:h-4 md:w-4 md:flex md:items-center',
  sm: 'md:-ml-0.5 md:mr-2 md:h-4 md:w-4 md:flex md:items-center',
  md: 'md:-ml-1 md:mr-2 md:h-5 md:w-5 md:flex md:items-center',
  lg: 'md:-ml-1 md:mr-3 md:h-5 md:w-5 md:flex md:items-center',
  xl: 'md:-ml-1 md:mr-3 md:h-5 md:w-5 md:flex md:items-center',
  xxl: 'md:-ml-1 md:mr-4 md:h-6 md:w-6 md:flex md:items-center',
};

const getLeadingTablet = tablet => (tablet ? leadingTabletMap[tablet] : null);

const leadingDesktopMap = {
  xs: 'lg:-ml-0.5 lg:mr-2 lg:h-4 lg:w-4 lg:flex lg:items-center',
  sm: 'lg:-ml-0.5 lg:mr-2 lg:h-4 lg:w-4 lg:flex lg:items-center',
  md: 'lg:-ml-1 lg:mr-2 lg:h-5 lg:w-5 lg:flex lg:items-center',
  lg: 'lg:-ml-1 lg:mr-3 lg:h-5 lg:w-5 lg:flex lg:items-center',
  xl: 'lg:-ml-1 lg:mr-3 lg:h-5 lg:w-5 lg:flex lg:items-center',
  xxl: 'lg:-ml-1 lg:mr-4 lg:h-6 lg:w-6 lg:flex lg:items-center',
};

const getLeadingDesktop = desktop =>
  desktop ? leadingDesktopMap[desktop] : null;

const leadingWidescreenMap = {
  xs: 'xl:-ml-0.5 xl:mr-2 xl:h-4 xl:w-4 xl:flex xl:items-center',
  sm: 'xl:-ml-0.5 xl:mr-2 xl:h-4 xl:w-4 xl:flex xl:items-center',
  md: 'xl:-ml-1 xl:mr-2 xl:h-5 xl:w-5 xl:flex xl:items-center',
  lg: 'xl:-ml-1 xl:mr-3 xl:h-5 xl:w-5 xl:flex xl:items-center',
  xl: 'xl:-ml-1 xl:mr-3 xl:h-5 xl:w-5 xl:flex xl:items-center',
  xxl: 'xl:-ml-1 xl:mr-4 xl:h-6 xl:w-6 xl:flex xl:items-center',
};

const getLeadingWidescreen = widescreen =>
  widescreen ? leadingWidescreenMap[widescreen] : null;

// Trailing icon styles - the icon is after the button's text

const trailingBaseMap = {
  xs: 'ml-2 -mr-0.5 h-4 w-4 flex items-center',
  sm: 'ml-2 -mr-0.5 h-4 w-4 flex items-center',
  md: 'ml-2 -mr-1 h-5 w-5 flex items-center',
  lg: 'ml-3 -mr-1 h-5 w-5 flex items-center',
  xl: 'ml-3 -mr-1 h-5 w-5 flex items-center',
  xxl: 'ml-4 -mr-1 h-6 w-6 flex items-center',
};

const getTrailingBase = base => trailingBaseMap[base];

const trailingMobileMap = {
  xs: 'sm:ml-2 sm:-mr-0.5 sm:h-4 sm:w-4 sm:flex sm:items-center',
  sm: 'sm:ml-2 sm:-mr-0.5 sm:h-4 sm:w-4 sm:flex sm:items-center',
  md: 'sm:ml-2 sm:-mr-1 sm:h-5 sm:w-5 sm:flex sm:items-center',
  lg: 'sm:ml-3 sm:-mr-1 sm:h-5 sm:w-5 sm:flex sm:items-center',
  xl: 'sm:ml-3 sm:-mr-1 sm:h-5 sm:w-5 sm:flex sm:items-center',
  xxl: 'sm:ml-4 sm:-mr-1 sm:h-6 sm:w-6 sm:flex sm:items-center',
};

const getTrailingMobile = mobile => (mobile ? trailingMobileMap[mobile] : null);

const trailingTabletMap = {
  xs: 'md:ml-2 md:-mr-0.5 md:h-4 md:w-4 md:flex md:items-center',
  sm: 'md:ml-2 md:-mr-0.5 md:h-4 md:w-4 md:flex md:items-center',
  md: 'md:ml-2 md:-mr-1 md:h-5 md:w-5 md:flex md:items-center',
  lg: 'md:ml-3 md:-mr-1 md:h-5 md:w-5 md:flex md:items-center',
  xl: 'md:ml-3 md:-mr-1 md:h-5 md:w-5 md:flex md:items-center',
  xxl: 'md:ml-4 md:-mr-1 md:h-6 md:w-6 md:flex md:items-center',
};

const getTrailingTablet = tablet => (tablet ? trailingTabletMap[tablet] : null);

const trailingDesktopMap = {
  xs: 'lg:ml-2 lg:-mr-0.5 lg:h-4 lg:w-4 lg:flex lg:items-center',
  sm: 'lg:ml-2 lg:-mr-0.5 lg:h-4 lg:w-4 lg:flex lg:items-center',
  md: 'lg:ml-2 lg:-mr-1 lg:h-5 lg:w-5 lg:flex lg:items-center',
  lg: 'lg:ml-3 lg:-mr-1 lg:h-5 lg:w-5 lg:flex lg:items-center',
  xl: 'lg:ml-3 lg:-mr-1 lg:h-5 lg:w-5 lg:flex lg:items-center',
  xxl: 'lg:ml-4 lg:-mr-1 lg:h-6 lg:w-6 lg:flex lg:items-center',
};

const getTrailingDesktop = desktop =>
  desktop ? trailingDesktopMap[desktop] : null;

const trailingWidescreenMap = {
  xs: 'xl:ml-2 xl:-mr-0.5 xl:h-4 xl:w-4 xl:flex xl:items-center',
  sm: 'xl:ml-2 xl:-mr-0.5 xl:h-4 xl:w-4 xl:flex xl:items-center',
  md: 'xl:ml-2 xl:-mr-1 xl:h-5 xl:w-5 xl:flex xl:items-center',
  lg: 'xl:ml-3 xl:-mr-1 xl:h-5 xl:w-5 xl:flex xl:items-center',
  xl: 'xl:ml-3 xl:-mr-1 xl:h-5 xl:w-5 xl:flex xl:items-center',
  xxl: 'xl:ml-4 xl:-mr-1 xl:h-6 xl:w-6 xl:flex xl:items-center',
};

const getTrailingWidescreen = widescreen =>
  widescreen ? trailingWidescreenMap[widescreen] : null;

export default function ButtonIcon(props) {
  const { icon, base, mobile, tablet, desktop, widescreen, position } = props;

  if (position === 'leading') {
    const leadingIconStyles = [
      getLeadingBase(base),
      getLeadingMobile(mobile),
      getLeadingTablet(tablet),
      getLeadingDesktop(desktop),
      getLeadingWidescreen(widescreen),
    ]
      .filter(Boolean)
      .join(' ');

    return <span className={leadingIconStyles}>{icon}</span>;
  }

  if (position === 'trailing') {
    const trailingIconStyles = [
      getTrailingBase(base),
      getTrailingMobile(mobile),
      getTrailingTablet(tablet),
      getTrailingDesktop(desktop),
      getTrailingWidescreen(widescreen),
    ]
      .filter(Boolean)
      .join(' ');

    return <span className={trailingIconStyles}>{icon}</span>;
  }
}

ButtonIcon.defaultProps = {
  base: 'xl',
  mobile: null,
  tablet: null,
  desktop: null,
  widescreen: null,
};

ButtonIcon.propTypes = {
  icon: PropTypes.node.isRequired,
  base: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', 'xxl']),
  mobile: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', 'xxl']),
  tablet: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', 'xxl']),
  desktop: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', 'xxl']),
  widescreen: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', 'xxl']),
  position: PropTypes.oneOf(['leading', 'trailing']).isRequired,
};
